import { IClient, QuestionCategories } from "@sp-crm/core";
import { AiSummary } from "components/ai-summary/ai-summary";
import { AntiFeature, Feature } from "components/feature";
import { Notes } from "components/notes";
import { NgGrid } from "components/shared/grid";
import { UnifiedTimeline } from "components/unified-timeline/unified-timeline";
import { WorkflowEntityExecutions } from "components/workflow/workflow-entity-executions";
import React from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ClientAgreementSection } from "./client-agreement-section";
import { ClientBestContact } from "./client-best-contact";
import { ClientCurrentLocation } from "./client-current-location";
import { ClientInfoForm } from "./client-info-form";
import { ClientReferralSource } from "./client-referral-source";
import { LegacyPowerOfAttorney } from "./power-of-attorney";
import ClientQuestionSection from "./question-section";

interface ClientOverviewTabProps {
    client: IClient;
    onFieldChange: (fieldName: string, newValue: string) => void;
    onFieldChangeMulti: (fieldValues: Record<string, unknown>) => void;
    onSendDisclosure: () => void;
}

const InteractionNotes: React.FC<{ client: IClient }> = ({ client }) => {
    return (
        <>
            <Feature name="unifiedTimeline">
                <UnifiedTimeline entityId={client.id} entityType="Client" />
            </Feature>
            <AntiFeature name="unifiedTimeline">
                <Notes entityId={client.id} entityType="Client" />
            </AntiFeature>
        </>
    );
};

export const ClientOverviewTab: React.FC<ClientOverviewTabProps> = props => {
    const { client, onFieldChange, onFieldChangeMulti, onSendDisclosure } = props;
    const {
        settings: { showClientFirst },
    } = useTenantSettings();
    const responsiveMode = useResponsiveMode();
    const isMobile = responsiveMode < ResponsiveMode.large;
    return (
        <NgGrid>
            <NgGrid.Column>
                {isMobile ? <InteractionNotes client={client} /> : null}
                {showClientFirst ? (
                    <>
                        <ClientInfoForm
                            client={client}
                            onFieldChange={onFieldChange}
                            onMultiFieldChange={onFieldChangeMulti}
                        />
                        <ClientCurrentLocation
                            client={client}
                            onFieldChange={onFieldChange}
                        />
                        <ClientBestContact
                            client={client}
                            onFieldChangeMulti={onFieldChangeMulti}
                        />
                    </>
                ) : (
                    <>
                        <ClientBestContact
                            client={client}
                            onFieldChangeMulti={onFieldChangeMulti}
                        />
                        <ClientInfoForm
                            client={client}
                            onFieldChange={onFieldChange}
                            onMultiFieldChange={onFieldChangeMulti}
                        />
                        <ClientCurrentLocation
                            client={client}
                            onFieldChange={onFieldChange}
                        />
                    </>
                )}
                <LegacyPowerOfAttorney client={client} onFieldChange={onFieldChange} />
            </NgGrid.Column>
            <NgGrid.Column>
                <AiSummary clientId={client.id} />
                {isMobile ? null : <InteractionNotes client={client} />}
                <ClientQuestionSection
                    category={QuestionCategories.summary}
                    clientId={client.id}
                />
                <ClientReferralSource client={client} onFieldChange={onFieldChange} />
                <ClientAgreementSection
                    client={client}
                    onFieldChange={onFieldChange}
                    onSendDisclosure={onSendDisclosure}
                />
                <WorkflowEntityExecutions entity={client} />
            </NgGrid.Column>
        </NgGrid>
    );
};
